import { createSelector } from 'reselect';

import { RootModel } from '../models';
import {
  FloorTypes,
  FocusItemTypes,
  ImageMapAreaTypes,
  ProjectItemTypes,
  ProjectLayoutTypes,
  ProjectTypes,
  StoreImageTypes,
  FacilityTypes,
} from '../../types';
import { sortFloorByFloorNumber } from '../../utils';
import {
  AREA_SHAPE,
  CONTENT_KEY,
  DEFAULT_AREA_COLOR,
  NONE_GROUP_FACILITY_IN_AMENITY,
  PROJECT_ITEM_TYPE,
} from '../../commons/constants';
import {
  BuildingLayoutTypes,
  VillaBlockLayoutTypes,
  AmenitiesLayoutTypes,
  AmenityLayoutConfigTypes,
  AmenitiesOverviewLayoutTypes,
} from '../../types/layout';
import { isValidAmenity } from '../../utils/logicUtils';

export const getSelectedProjectItem = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  number,
  ProjectItemTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.sitePlanModel.projectItemId,
  (ids, id) => ids[id]
);

export const getSelectedAmenity = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  number,
  ProjectItemTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.amenitiesModel.projectItemId,
  (ids, id) => ids[id]
);

type ProjectItemLayoutConfigTypes = {
  layoutImage?: StoreImageTypes;
  layoutItems?: VillaBlockLayoutTypes[] | BuildingLayoutTypes[] | AmenitiesLayoutTypes[];
  areas?: ImageMapAreaTypes[];
};

export const getProjectItemLayoutConfig = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  { [key: string]: FloorTypes },
  { [key: string]: FacilityTypes },
  number,
  string,
  number,
  number,
  string,
  boolean,
  ProjectItemLayoutConfigTypes
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.floorEntity.ids,
  (state) => state.facilityEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.projectModel.contentActiveKey,
  (state) => state.sitePlanModel.projectItemId,
  (state) => state.amenitiesModel.projectItemId,
  (state) => state.amenitiesModel.groupFacilityKey,
  (state) => state.layoutModel.isMobileDevice,
  (
    projectIds,
    projectItemIds,
    floorIds,
    facilities,
    projectId,
    contentActiveKey,
    projectItemId,
    projectItemIdByAmenity,
    groupFacilityKey,
    isMobileDevice
  ) => {
    const project: ProjectTypes = projectIds[projectId];

    let projectItem: ProjectItemTypes | null;

    if (contentActiveKey === CONTENT_KEY.AMENITIES.key) {
      projectItem = projectItemIds[projectItemIdByAmenity];
    } else if (contentActiveKey === CONTENT_KEY.SITEPLAN.key) {
      projectItem = projectItemIds[projectItemId];
    }

    if (!projectItem) return {};
    const areas = [];

    if (projectItem.type === PROJECT_ITEM_TYPE.BUILDING) {
      if (!projectItem.layoutItems) return {};

      const layoutItems = projectItem.layoutItems.map((layoutItem: BuildingLayoutTypes) => {
        const item = floorIds[layoutItem.floorId];

        if (item && !item.isDisabled) {
          areas.push({
            id: layoutItem.floorId,
            dataKey: `${item.code}/${item.alias}`,
            coords: layoutItem.zoneCoords,
            shape: AREA_SHAPE.POLY,
            color: layoutItem.zoneColor || DEFAULT_AREA_COLOR,
            labelCoords: layoutItem.labelCoords,
            labelColor: layoutItem.labelColor,
            transparent: layoutItem.transparent,
          });
        }

        return { ...layoutItem, item };
      });

      return { layoutImage: projectItem.layoutImage, layoutItems, areas };
    }

    if (projectItem.type === PROJECT_ITEM_TYPE.VILLA_BLOCK) {
      const layoutItems = project.layoutItems.map((layoutItem: ProjectLayoutTypes) => {
        const item = projectItemIds[layoutItem.projectItemId];

        if (item && !item.isDisabled) {
          areas.push({
            id: layoutItem.projectItemId,
            dataKey: `${item.code}/${item.alias}`,
            coords: layoutItem.zoneCoords,
            shape: AREA_SHAPE.POLY,
            color: layoutItem.zoneColor || DEFAULT_AREA_COLOR,
            labelCoords: layoutItem.labelCoords,
            labelColor: layoutItem.labelColor,
            transparent: layoutItem.transparent,
          });
        }

        return { ...layoutItem, item };
      });

      return { layoutImage: project.layoutImage, layoutItems, areas };
    }

    if (projectItem.type === PROJECT_ITEM_TYPE.AMENITY) {
      if (!projectItem.layoutItems) return {};

      let layoutItems: FacilityTypes[];
      // get group facility
      if (groupFacilityKey && groupFacilityKey !== NONE_GROUP_FACILITY_IN_AMENITY.key) {
        const item = facilities[groupFacilityKey];
        if (item && item.facilityCodes && item.facilityCodes.length) {
          layoutItems = item.facilityCodes.map((data) => {
            const facility = facilities[data];
            if (facility) {
              const facilityArea = convertFacilityArea(facility);
              areas.push(facilityArea);
            }
            return { ...facility };
          });
        }
      } else if (
        // get none group facility in amenity
        groupFacilityKey &&
        isMobileDevice &&
        groupFacilityKey === NONE_GROUP_FACILITY_IN_AMENITY.key
      ) {
        const items = [];
        projectItem.layoutItems.forEach((layoutItem) => {
          const item = facilities[layoutItem.code];
          if (item && item.numberOfFacilities === 0) {
            items.push({ ...layoutItem, ...item });
            if (item) {
              const facilityArea = convertFacilityArea(item);
              areas.push(facilityArea);
            }
          }
        });
        layoutItems = items;
      } else {
        // get facility in amenity
        layoutItems = projectItem.layoutItems.map((layoutItem: AmenitiesLayoutTypes) => {
          const item = facilities[layoutItem.code];
          if (item) {
            const facilityArea = convertFacilityArea(item);
            areas.push(facilityArea);
          }
          return { ...layoutItem, ...item };
        });
      }

      if (layoutItems && layoutItems.length) {
        layoutItems = layoutItems.sort((a) => (!a.numberOfFacilities ? -1 : 0));
      }

      return {
        layoutImage: projectItem.layoutImage,
        layoutItems,
        areas,
      };
    }
  }
);

const convertFacilityArea = (item: FacilityTypes) => {
  const facilityArea = {
    id: item.id,
    dataKey: item.code,
    coords: item.zoneCoords,
    shape: AREA_SHAPE.POLY,
    color: item.zoneColor || DEFAULT_AREA_COLOR,
    labelCoords: item.labelCoords,
    labelColor: item.labelColor,
    transparent: item.transparent,
    labelTransparent: item.labelTransparent,
  };
  return facilityArea;
};

export const getProjectItemsByType = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  number,
  string,
  { items: ProjectItemTypes[]; rating?: string }
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.projectModel.contentActiveKey,
  (projectIds, projectItemIds, projectId, contentActiveKey) => {
    const project = projectIds[projectId];
    if (!project) return { items: null };

    const items: ProjectItemTypes[] = [];
    const ratingValues: number[] = [];

    project.itemIds.forEach((id) => {
      const item: ProjectItemTypes = projectItemIds[id];
      if (!item) return;

      switch (contentActiveKey) {
        // case amenity
        case CONTENT_KEY.AMENITIES.key:
          if (isValidAmenity(item)) {
            items.push(item);
            if (item.rating) ratingValues.push(item.rating);
          }
          break;
        // case site plan
        case CONTENT_KEY.SITEPLAN.key:
          if (
            item.type === PROJECT_ITEM_TYPE.BUILDING ||
            item.type === PROJECT_ITEM_TYPE.VILLA_BLOCK
          ) {
            items.push(item);
          }
          break;

        default:
          break;
      }
    });

    return {
      items: items.sort((a, b) => a.orderIndex - b.orderIndex),
      rating:
        ratingValues && !!ratingValues.length
          ? (ratingValues.reduce((a, b) => a + b) / ratingValues.length).toFixed(1)
          : null,
    };
  }
);

export const getItemList = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  { [key: string]: FloorTypes },
  number,
  number,
  FloorTypes[] | ProjectItemTypes[]
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.floorEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.sitePlanModel.projectItemId,

  (projectIds, projectItemIds, floorIds, projectId, projectItemId) => {
    const projectItem = projectItemIds[projectItemId];
    if (!projectItem || !projectItem.itemIds) return null;

    if (projectItem.type === PROJECT_ITEM_TYPE.BUILDING) {
      const floors: FloorTypes[] = [];

      projectItem.itemIds.forEach((id) => {
        const floor = floorIds[id];
        if (floor) floors.push(floor);
      });

      return sortFloorByFloorNumber(floors) as FloorTypes[];
    }

    if (projectItem.type === PROJECT_ITEM_TYPE.VILLA_BLOCK) {
      const items: ProjectItemTypes[] = [];

      projectIds[projectId].itemIds.forEach((id) => {
        const item = projectItemIds[id];
        if (item) items.push(item);
      });

      return items;
    }

    return null;
  }
);

export const geSelectedProjectItem = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  FocusItemTypes,
  ProjectItemTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.sitePlanModel.focusProjectItem,
  (ids, focusItem) => {
    if (!focusItem || !focusItem.id) return null;

    return ids[focusItem.id];
  }
);

export const getFocusProjectItem = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  FocusItemTypes,
  ProjectItemTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.sitePlanModel.focusProjectItem,
  (ids, focusProjectItem) => {
    if (!focusProjectItem || !focusProjectItem.id || !focusProjectItem.isShowModal) return null;

    return ids[focusProjectItem.id];
  }
);

// only using for desktop visualization content focus feature
export const getFocusItem = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  number,
  number,
  FocusItemTypes
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.sitePlanModel.projectItemId,
  (projectIds, projectItemIds, projectId, projectItemId) => {
    const projectItem = projectItemIds[projectItemId];
    if (!projectItem) return null;

    if (projectItem.type === PROJECT_ITEM_TYPE.BUILDING) {
      return null;
    }

    if (projectItem.type === PROJECT_ITEM_TYPE.VILLA_BLOCK) {
      const project = projectIds[projectId];
      if (!project) return null;

      const layoutItem = project.layoutItems.find((i) => i.projectItemId === +projectItemId);
      if (!layoutItem) return null;

      return { id: layoutItem.projectItemId, labelCoords: layoutItem.labelCoords };
    }
  }
);

export const getFocusFacilityItem = createSelector<
  RootModel,
  { [key: string]: FacilityTypes },
  string,
  FacilityTypes
>(
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesModel.focusFacilityKey,
  (facilityEntityIds, focusFacilityKey) => {
    if (focusFacilityKey) return facilityEntityIds[focusFacilityKey];
    return null;
  }
);

export const getProjectItem = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  number,
  ProjectItemTypes | null
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.sitePlanModel.projectItemId,
  (ids, id) => ids[id]
);

export const getAmenities = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  ProjectItemTypes[]
>(
  (state) => state.projectItemEntity.ids,
  (ids) => {
    const amenities = (Object.values(ids) || []).filter(
      (item) => item.type === PROJECT_ITEM_TYPE.AMENITY
    );
    // filter amenity have facility
    const filterAmenities = amenities.filter(
      (item) => item?.facilityCodes?.length || item?.groupFacilityCodes?.length
    );
    return filterAmenities;
  }
);

export const getAmenityOverview = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  string,
  ProjectItemTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.amenitiesOverviewModel.amenityKey,
  (ids, amenityKey) => {
    const amenity = (Object.values(ids) || []).find((item) => item.code === amenityKey);
    return amenity;
  }
);

export const getAmenityOverviewLayoutConfig = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  { [key: string]: FacilityTypes },
  string,
  AmenityLayoutConfigTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesOverviewModel.amenityKey,
  (projectItemEntity, facilityEntity, amenityKey) => {
    const amenity = Object.values(projectItemEntity).find((item) => item.code === amenityKey);

    if (!amenity) return;

    const areas: ImageMapAreaTypes[] = [];
    let layoutItems: AmenitiesOverviewLayoutTypes[];

    layoutItems = amenity?.layoutItems?.map((layoutItem) => {
      const item = facilityEntity[layoutItem.code];
      if (item) {
        const facilityArea = convertFacilityArea(item);
        areas.push(facilityArea);
      }
      return { ...layoutItem, ...item };
    });

    if (layoutItems && layoutItems.length) {
      layoutItems = layoutItems.sort((a) => (!a.numberOfFacilities ? -1 : 0));
    }

    return {
      layoutImage: amenity?.layoutImage,
      layoutItems,
      areas,
    };
  }
);
